import React, { useEffect, useState } from "react";
import { supabase } from "../../Services/supabaseClient";
import { useCompany } from "../../context/CompanyContext";
import { Paperclip, UploadCloud, ExternalLink, Trash2 } from "lucide-react";
import { uploadTradeAttachment } from "./uploadAttachment";

export default function AttachmentsPanel({ entityType, entityId }) {
  const { companyId } = useCompany();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);

  async function load() {
    setErr("");
    if (!companyId || !entityId) return;

    const { data, error } = await supabase
      .from("transaction_attachments")
      .select("*")
      .eq("company_id", companyId)
      .eq("entity_type", entityType)
      .eq("entity_id", entityId)
      .order("created_at", { ascending: false });

    if (error) setErr(error.message);
    setRows(data || []);
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, entityType, entityId]);

  async function onPick(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!companyId || !entityId) return;

    setBusy(true);
    setErr("");
    try {
      await uploadTradeAttachment({
        company_id: companyId,
        entity_type: entityType,
        entity_id: entityId,
        file,
      });
      await load();
    } catch (ex) {
      setErr(ex?.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  }

  async function removeRow(id) {
    if (
      !window.confirm(
        "Delete this attachment record? (File remains in storage)"
      )
    )
      return;
    const { error } = await supabase
      .from("transaction_attachments")
      .delete()
      .eq("company_id", companyId)
      .eq("id", id);

    if (error) return setErr(error.message);
    setRows((p) => p.filter((x) => x.id !== id));
  }

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div
          style={{
            fontWeight: 900,
            display: "flex",
            gap: 8,
            alignItems: "center",
          }}
        >
          <Paperclip size={16} />
          Attachments
        </div>

        <label
          className="btn"
          style={{ marginLeft: "auto", opacity: busy ? 0.6 : 1 }}
        >
          <UploadCloud size={16} /> {busy ? "Uploading…" : "Upload"}
          <input
            type="file"
            onChange={onPick}
            disabled={busy}
            style={{ display: "none" }}
          />
        </label>
      </div>

      {err && (
        <div className="banner error" style={{ marginTop: 10 }}>
          {err}
        </div>
      )}

      {rows.length === 0 ? (
        <div className="muted" style={{ marginTop: 10, fontSize: 13 }}>
          No attachments yet.
        </div>
      ) : (
        <div style={{ marginTop: 10, display: "grid", gap: 8 }}>
          {rows.map((r) => (
            <div
              key={r.id}
              className="panel"
              style={{
                padding: 10,
                display: "flex",
                gap: 10,
                alignItems: "center",
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800 }}>{r.file_name}</div>
                <div className="muted" style={{ fontSize: 12 }}>
                  {r.content_type || "file"} •{" "}
                  {Number(r.file_size || 0).toLocaleString()} bytes
                </div>
              </div>

              <button
                className="icon-btn"
                title="Open in Storage (path)"
                onClick={() => {
                  navigator.clipboard.writeText(`${r.bucket}/${r.object_path}`);
                  alert("Copied storage path to clipboard.");
                }}
              >
                <ExternalLink size={16} />
              </button>

              <button
                className="icon-btn danger"
                title="Remove record"
                onClick={() => removeRow(r.id)}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
